import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import gql from "graphql-tag";
import { useQuery, useMutation } from "@apollo/client";

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Link as MuiLink,
  IconButton,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Checkbox,
  CircularProgress,
  Box,
  Stack,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import AdapterLink from "../components/form/Link";

const FILE_DOWNLOAD = gql`
  mutation FileIncidentDownload($input: FileIncidentDownloadPdfInput!) {
    incidents {
      downloadPdf(input: $input) {
        pdfBase64
        fileName
      }
    }
  }
`;

const POLICY_QUERY = gql`
  query Policies {
    policies {
      all {
        edges {
          node {
            id
            number
            insurerName
          }
        }
      }
    }
  }
`;

const FileIncidentDownloadDialog = ({
  incidentId,
  onClose,
  open,
  hasEvidencePendingReview,
}) => {
  const [working, setWorking] = useState(false);
  const [error, setError] = useState(null);

  const [policyId, setPolicyId] = useState(null);

  const [includePolicy, setIncludePolicy] = useState(false);
  const [fileAnywayConfirmed, setFileAnywayConfirmed] = useState(false);

  const policyQuery = useQuery(POLICY_QUERY, {
    fetchPolicy: "cache-and-network",
  });

  const edges = policyQuery?.data?.policies?.all?.edges ?? [];
  const loading = policyQuery.loading;

  const [fileDownload] = useMutation(FILE_DOWNLOAD);

  useEffect(() => {
    if (includePolicy && !policyId && edges.length > 0) {
      setPolicyId(edges[0].node.id);
    }
  }, [includePolicy, edges, policyId]);

  const handleSubmit = () => {
    setError(null);

    if (hasEvidencePendingReview && !fileAnywayConfirmed) return;

    if (includePolicy && !policyId) {
      setError("Select a policy");
      return;
    }

    const variables = {
      input: {
        incidentId,
        policy: includePolicy ? { id: policyId } : null,
      },
    };

    setWorking(true);

    fileDownload({ variables })
      .then(({ data }) => {
        setWorking(false);

        const pdf = data?.incidents?.downloadPdf;
        if (!pdf) {
          setError("Could not generate PDF");
          return;
        }

        const bytes = atob(pdf.pdfBase64)
          .split("")
          .map((c) => c.charCodeAt(0));
        const blob = new Blob([new Uint8Array(bytes)], {
          type: "application/pdf",
        });

        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = pdf.fileName ?? "incident.pdf";
        a.click();
        URL.revokeObjectURL(url);

        onClose();
      })
      .catch((err) => {
        setWorking(false);
        setError(err.message);
      });
  };

  return (
    <Dialog fullWidth open={open} onClose={onClose}>
      <DialogTitle>
        Download incident
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Stack>
          <FormControlLabel
            control={
              <Checkbox
                checked={includePolicy}
                onChange={(e) => setIncludePolicy(e.target.checked)}
              />
            }
            label="Include insurance policy details?"
          />

          {includePolicy && (
            <Box sx={{ pl: 4, pt: 1 }}>
              {loading && (
                <DialogContentText>Loading policies...</DialogContentText>
              )}

              {!loading && edges.length > 0 && (
                <FormControl component="fieldset" fullWidth>
                  <FormLabel>Policy</FormLabel>
                  <RadioGroup
                    value={policyId}
                    onChange={(e) => setPolicyId(e.target.value)}
                  >
                    {edges.map(({ node }) => (
                      <FormControlLabel
                        key={node.id}
                        value={node.id}
                        control={<Radio />}
                        label={`${node.insurerName}: ${node.number}`}
                      />
                    ))}
                  </RadioGroup>
                </FormControl>
              )}

              {!loading && edges.length === 0 && (
                <DialogContentText>
                  No policies found. Please{" "}
                  <MuiLink component={AdapterLink} to="/admin/insurer-policy">
                    add policy details
                  </MuiLink>{" "}
                  and try again.
                </DialogContentText>
              )}
            </Box>
          )}

          {hasEvidencePendingReview && (
            <Box>
              <FormControl required error={!fileAnywayConfirmed}>
                <FormLabel>
                  The download will not include evidence still pending review.
                </FormLabel>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={fileAnywayConfirmed}
                      onChange={(e) => setFileAnywayConfirmed(e.target.checked)}
                    />
                  }
                  label="Download anyway"
                />
              </FormControl>
            </Box>
          )}

          {/* Error */}
          {error && (
            <DialogContentText sx={{ color: "error.main" }}>
              {error}
            </DialogContentText>
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button disabled={working} onClick={onClose}>
          Cancel
        </Button>

        <Button
          variant="contained"
          disabled={
            working || (hasEvidencePendingReview && !fileAnywayConfirmed)
          }
          onClick={handleSubmit}
          startIcon={working ? <CircularProgress size={18} /> : null}
        >
          {working ? "Generating..." : "Download"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

FileIncidentDownloadDialog.propTypes = {
  incidentId: PropTypes.string.isRequired,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  hasEvidencePendingReview: PropTypes.bool.isRequired,
};

export default FileIncidentDownloadDialog;
